// TOOL screen — the tool picked in the orb, plus a vertical cycle through the
// three tools of its gear (DEMI → TYTN → OLMPN, wrapping). Gold ▲ / ▼ (or a
// swipe on their strips) slide the current tool out and the next one in.
import React, { useRef } from 'react';
import { Animated, Easing, PanResponder, Pressable, StyleSheet, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import type { Gear } from '../domain/olmps';
import { GEAR_TIERS, TIER_ORDER, tierOf, type DmtToolIdLike } from './toolTiers';
import { ToolPanel } from '../olmps/ToolPanel';
import { GEAR_LABEL, SEAT_BY_ID } from './roster';
import { EdgeTabs } from './EdgeTabs';
import { makeStyles } from './theme';

const STRIP = 62;

export function ToolScreen({
  toolId,
  toolOpts,
  fallbackGear,
  width,
  height,
  top,
  bottom,
  onSelectTool,
  onBack,
  onOpenTool,
}: {
  toolId: string;
  toolOpts?: { initialTaskId?: string };
  fallbackGear: Gear;
  width: number;
  height: number;
  top: number;
  bottom: number;
  onSelectTool: (shellId: string) => void;
  onBack: () => void;
  onOpenTool: (id: string, opts?: { initialTaskId?: string }) => void;
}) {
  const styles = useStyles();
  const dmt: DmtToolIdLike = toolId === 'astrs' ? 'astra' : toolId === 'jnus' ? 'janus' : toolId;
  const where = tierOf(dmt as Parameters<typeof tierOf>[0]);
  const ids = where ? TIER_ORDER.map((tier) => GEAR_TIERS[where.gear][tier]) : [];
  const idx = where ? TIER_ORDER.indexOf(where.tier) : -1;
  const shell = (d: string) => SEAT_BY_ID[d]?.shellTool ?? d;
  const gear = (where?.gear ?? fallbackGear) as Gear;

  const sy = useRef(new Animated.Value(0)).current;
  const busy = useRef(false);
  const live = useRef({ idx, ids, height });
  live.current = { idx, ids, height };
  const cycle = (dir: 1 | -1) => {
    const { idx: cur, ids: list, height: h } = live.current;
    if (busy.current || cur < 0 || list.length < 2) return;
    busy.current = true;
    const next = list[(cur + dir + list.length) % list.length];
    // ▼ (dir +1): current slides up and out, the next rises in from below.
    Animated.timing(sy, { toValue: -dir * h * 0.6, duration: 170, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => {
      onSelectTool(shell(next));
      sy.setValue(dir * h * 0.6);
      Animated.timing(sy, { toValue: 0, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start(() => {
        busy.current = false;
      });
    });
  };
  const cycleRef = useRef(cycle);
  cycleRef.current = cycle;
  const strip = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dy) > 12 && Math.abs(g.dy) > Math.abs(g.dx),
      onPanResponderRelease: (_e, g) => {
        if (g.dy < -40) cycleRef.current(1);
        else if (g.dy > 40) cycleRef.current(-1);
      },
    }),
  ).current;

  const cycling = where != null;
  return (
    <View style={StyleSheet.absoluteFill}>
      <View style={{ position: 'absolute', left: 56, right: 56, top: top + (cycling ? STRIP + 18 : 0), bottom: bottom + (cycling ? STRIP : 0), overflow: 'hidden' }}>
        <Animated.View style={{ flex: 1, transform: [{ translateY: sy }] }}>
          <ToolPanel
            key={`${toolId}:${toolOpts?.initialTaskId ?? ''}`}
            sector="H"
            stage="T"
            gear={gear}
            toolIds={[toolId]}
            activeToolIndex={0}
            onSelectToolIndex={() => {}}
            onClose={onBack}
            onOpenInfo={() => {}}
            toolProps={toolOpts}
            onOpenTool={onOpenTool}
          />
        </Animated.View>
      </View>
      {cycling ? (
        <View style={[styles.kick, { top: top + 56 }]} pointerEvents="none">
          <View style={styles.pips}>
            {ids.map((_, i) => (
              <View key={i} style={[styles.pip, i === idx && styles.pipOn]} />
            ))}
          </View>
          <Text style={styles.kickText} numberOfLines={1}>
            {GEAR_LABEL[where!.gear].name.toUpperCase()} · {(SEAT_BY_ID[dmt]?.name ?? dmt).toUpperCase()}
          </Text>
        </View>
      ) : null}
      <EdgeTabs
        top={top}
        bottom={bottom}
        left={onBack}
        up={cycling ? () => cycle(-1) : undefined}
        down={cycling ? () => cycle(1) : undefined}
        labels={{ up: 'Previous tool', down: 'Next tool', left: 'Back to the orb' }}
      />
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    kick: { position: 'absolute', left: 0, right: 0, alignItems: 'center', gap: 4 },
    pips: { flexDirection: 'row', gap: 4 },
    pip: { width: 6, height: 6, borderRadius: 3, backgroundColor: t.dotOff },
    pipOn: { backgroundColor: t.goldBright, width: 14 },
    kickText: { color: t.goldBright, fontSize: 10.5, fontWeight: '800', letterSpacing: 1.6, maxWidth: 260 },
  }),
);
